import {useMemo} from "react";
import _ from "lodash";
import {useProjectsContext} from "@/pages/dashboard/ProjectsContext";
import SingleSiteHeader from "./SiteFilter/SingleSiteHeader";
import MultiSiteFilter from "./SiteFilter/MultiSiteFilter";
import SelectedSitesList from "@/pages/dashboard/SiteFilter/SelectedSitesList";

/**
 * ProjectSiteFilter component
 *
 * This component provides a site filtering interface for projects.
 * It handles two main display modes:
 * 1. Single Site: Displays only the site name if only one site is available.
 * 2. Multi-Site: Displays a dropdown filter to select/deselect multiple sites.
 *
 * It also displays a list of currently selected sites with an option to reset filters
 * when in multi-site mode.
 */
export default function ProjectSiteFilter(props) {
  const {projects, selectedSiteIds, setSelectedSiteIds} = useProjectsContext();

  const siteCounts = useMemo(() => _.countBy(projects, 'siteId'), [projects]);

  const availableSites = useMemo(() => {
    const siteIds = _.uniq(_.map(projects, 'siteId'));
    return _.orderBy(siteIds.map(id => ({
      id,
      name: _.find(projects, ['siteId', id])?.siteName,
      projectCount: siteCounts[id]
    })), 'projectCount', 'desc');
  }, [projects]);

  const filteredSites = useMemo(() => {
    return _.orderBy(selectedSiteIds.map(id => ({
      id,
      name: _.find(projects, ['siteId', id])?.siteName,
      projectCount: siteCounts[id]
    })), 'projectCount', 'desc');
  }, [projects, selectedSiteIds]);

  const toggleSite = (siteId) => {
    setSelectedSiteIds(prev => {
      if (prev.includes(siteId)) {
        return prev.filter(id => id !== siteId);
      } else {
        return [...prev, siteId];
      }
    });
  };
  const clearSites = () => {
    setSelectedSiteIds([]);
  }

  if (!availableSites?.length) return null;

  return (
    <div className="w-full flex gap-2">
      <div className="shrink-0">
        {availableSites.length === 1 && (
          <SingleSiteHeader site={availableSites[0]}/>
        )}
        {availableSites.length > 1 && (
          <MultiSiteFilter
            id="project-site-filter-btn"
            selectedSiteIds={selectedSiteIds}
            availableSites={availableSites}
            toggleSite={toggleSite}
            clearSites={clearSites}
          />
        )}
      </div>
      {availableSites.length > 1 && (
        <SelectedSitesList
          filteredSites={filteredSites}
          toggleSite={toggleSite}
          clearSites={clearSites}
        />
      )}
    </div>
  )
}




